import { Component, Input, EventEmitter, InjectionToken, Injector, OnDestroy, OnInit } from '@angular/core';
import { ComponentPortal, PortalInjector, } from '@angular/cdk/portal';
import { select, Store, } from '@ngrx/store';
import {
  CATALOG_DATE_FACET_COMPONENT_DATA,
  CatalogDateFacetComponent,
} from 'filter-panel/components/catalog-date-facet/catalog-date-facet.component';
import { Subscription, } from 'rxjs';

import { getV2Facets, State, } from 'search/reducers/root.reducer';
import { FacetFilterBlock, } from 'search/models/filter-panel';
import { CatalogDateFacet, DateFacet, Facet, } from 'search/facets/models/resource-facet';
import { FacetSchema } from 'search/facets/models/facet-schema';
import { FacetType } from 'search/facets/models/facet-type';
import { FACET_COMPONENT_DATA, FacetComponent, } from 'filter-panel/components/facet/facet.component';
import {
  FACET_FILTER_COMPONENT_DATA,
  FacetFilterContainerComponent,
} from 'filter-panel/components/facet-filter/facet-filter-container.component';
import {
  DATE_FACET_COMPONENT_DATA,
  DateFacetComponent,
} from 'filter-panel/components/date-facet/date-facet.component';
import { FacetsSchemaService } from 'search/facets/services/facets-schema.service';
import { FacetState } from 'search/facets/models/facet-state';
import { EntityTypes } from '../../../entity/models/entity';

@Component({
  selector: 'app-filter-panel-container',
  template: `
    <app-filter-panel
      [facetControls]="facetControls"
      [isFacetsChanged]="isFacetsChanged"
      [isKidsCatalog]="isKidsCatalog">
    </app-filter-panel>
  `,
})
export class FilterPanelContainerComponent implements OnInit, OnDestroy {
  @Input() isKidsCatalog: boolean = false;
  public facetControls: FacetFilterBlock[];
  public isFacetsChanged: boolean;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly store: Store<State>,
    private readonly injector: Injector,
    private readonly facetSchemaService: FacetsSchemaService,
  ) {
  }

  public ngOnInit() {
    const facetsSchemas = this.facetSchemaService.getActualFacetSchemas();

    this.facetControls = this.initFacetControls(facetsSchemas);

    setTimeout(() => this.subscriptions.add(this.store.pipe(select(getV2Facets)).subscribe((allFacets) => {
      this.isFacetsChanged = this.detectFacetsChanges(allFacets);
      allFacets.forEach((facetState: FacetState) => {
        this.emitFacetData(facetState);
      });
    })), 0);

  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public getTermsCount(terms: any[][]) {
    if (!terms || !terms.length) {
      return 0;
    }
    return terms.reduce((sum, elem) => sum + elem.filter((term) => EntityTypes.FORMAT_GROUP !== term.entityType).length, 0);
  }

  private detectFacetsChanges(facetStates: FacetState[]): boolean {
    return facetStates.some((facetState: FacetState) => {
      switch (facetState.schemaWithData.schema.type) {
        case FacetType.Collections: {
          return;
        }
        case FacetType.Iterable: {
          const convertedFacetState = facetState as FacetState<Facet[]>;
          const selectedIds = convertedFacetState.selected.map((item: Facet) => item.id);
          const appliedIds = convertedFacetState.applied.map((item: Facet) => item.id);
          const uniqIds = new Set([...selectedIds, ...appliedIds]);
          return uniqIds.size !== selectedIds.length || uniqIds.size !== appliedIds.length;
        }
        case FacetType.Date: {
          const convertedFacetState = facetState as FacetState<DateFacet>;
          if (!convertedFacetState.applied && convertedFacetState.selected.selected) {
            return true;
          }
          if (convertedFacetState.applied && !convertedFacetState.selected.selected) {
            return true;
          }
          return convertedFacetState.applied && (convertedFacetState.applied.dateFrom !== convertedFacetState.selected.dateFrom
            || convertedFacetState.applied.dateTo !== convertedFacetState.selected.dateTo);

        }
        case FacetType.CatalogDate: {
          const convertedFacetState = facetState as FacetState<CatalogDateFacet>;
          if (!convertedFacetState.applied && convertedFacetState.selected.selected) {
            return true;
          }
          if (convertedFacetState.applied && !convertedFacetState.selected.selected) {
            return true;
          }
          return convertedFacetState.applied && convertedFacetState.applied.catalogDate !== convertedFacetState.selected.catalogDate;
        }
        case FacetType.Single: {
          const convertedFacetState = facetState as FacetState<Partial<Facet>>;
          return convertedFacetState.selected.selected && !convertedFacetState.applied
            || !convertedFacetState.selected.selected && !!convertedFacetState.applied;
        }
      }
    });
  }

  private initFacetControls(facetSchemas: FacetSchema[]): FacetFilterBlock[] {
    return facetSchemas
    .reduce((prev, schema) => {
      const dataEmitter = new EventEmitter<FacetState>();
      const key = schema.key;
      switch (schema.type) {
        case FacetType.Iterable:
          return prev.concat([{
            component: new ComponentPortal(FacetComponent, null,
              this.injectFacetData(FACET_COMPONENT_DATA, {
                facetData: dataEmitter,
                facetKey: key,
              })),
            dataEmitter,
            key,
          }]);
        case FacetType.Single:
          return prev.concat([{
            component: new ComponentPortal(FacetFilterContainerComponent, null,
              this.injectFacetData(FACET_FILTER_COMPONENT_DATA, dataEmitter)),
            dataEmitter,
            key,
          },
          ]);
        case FacetType.Date:
          return prev.concat([
            {
              component: new ComponentPortal(DateFacetComponent, null,
                this.injectFacetData(DATE_FACET_COMPONENT_DATA, {
                  facetData: dataEmitter,
                  facetKey: key,
                })),
              dataEmitter,
              key,
            },
          ]);
        case FacetType.CatalogDate:
          return prev.concat([
            {
              component: new ComponentPortal(CatalogDateFacetComponent, null,
                this.injectFacetData(CATALOG_DATE_FACET_COMPONENT_DATA, {
                  facetData: dataEmitter,
                  facetKey: key,
                })),
              dataEmitter,
              key,
            },
          ]);
      }
      return prev;
    }, []);
  }

  private injectFacetData(injectionToken: InjectionToken<any>, data: any) {
    const weakMap = new WeakMap();
    weakMap.set(injectionToken, data);
    return new PortalInjector(this.injector, weakMap);
  }

  private emitFacetData(facetState: FacetState) {
    if (facetState) {
      const schemaWithData = facetState.schemaWithData;
      const schema = schemaWithData.schema;

      const facetControl = this.facetControls.find((ctrl) => schema.key === ctrl.key);
      facetControl.dataEmitter.emit({
        ...facetState,
      });
    }
  }
}
